import "core-js/stable";
import "regenerator-runtime/runtime";

import React from "react";
import PropTypes from "prop-types";
import { Switch, Route, Redirect } from "react-router-dom";
import { QueryClientProvider } from "react-query";
import { Loading } from "@folio/stripes/components";
import {
  hasRole,
  useActiveRoles,
  hasAtLeastOneRole,
} from "./utils/permissionsUtils.tsx";
import { getFallbackRoute } from "./utils/routeUtils.ts";
import { createAgencyQueryClient } from "./queryClient.ts";
import { routesConfig, preferredFallbackRouteKey } from "./routesConfig";
import { RoleEnum } from "./types/RoleEnum.ts";
import Settings from "./settings";
import { AccessDenied } from "./components/AccessDenied.tsx";
import { PublisherDataRequired } from "./components/PublisherDataRequired.tsx";
import { PublisherDeclarationGate } from "./components/PublisherDeclarationGate.tsx";
import { usePublisherDataValidity } from "./hooks/usePublisherDataValidity.ts";

const queryClient = createAgencyQueryClient();

const AgencyRoutes = ({ activeRoles, path, connectedRoutes, parentProps }) => {
  const { needsDataConfirmation, isLoading } = usePublisherDataValidity();

  if (isLoading) {
    return <Loading />;
  }

  if (needsDataConfirmation) {
    return <PublisherDataRequired />;
  }

  return (
    <>
      <PublisherDeclarationGate />
      <Switch>
        {Object.keys(routesConfig).map((key) => {
          const { path: routePath, roles } = routesConfig[key];
          const Component = connectedRoutes[key];

          const isSuperUser = hasRole(activeRoles, RoleEnum.SUPERUSER);
          const hasRequiredRole = roles
            ? hasAtLeastOneRole(activeRoles, roles)
            : true;
          const hasAccess = isSuperUser || hasRequiredRole;

          return (
            <Route
              key={key}
              path={`${path}${routePath}`}
              render={(routeProps) =>
                !hasAccess ? (
                  <AccessDenied showNavigation />
                ) : (
                  <Component {...parentProps} {...routeProps} />
                )
              }
            />
          );
        })}
        <Route
          render={() => {
            const fallbackRoute = getFallbackRoute(
              activeRoles,
              routesConfig,
              preferredFallbackRouteKey,
            );

            if (fallbackRoute) {
              return <Redirect to={`${path}${fallbackRoute.path}`} />;
            }

            return <AccessDenied showNavigation />;
          }}
        />
      </Switch>
    </>
  );
};

AgencyRoutes.propTypes = {
  activeRoles: PropTypes.array,
  path: PropTypes.string.isRequired,
  connectedRoutes: PropTypes.object.isRequired,
  parentProps: PropTypes.object.isRequired,
};

const BrAgency = (props) => {
  const activeRoles = useActiveRoles();
  const {
    showSettings,
    match: { path },
    stripes,
  } = props;

  if (showSettings) {
    return <Settings {...props} />;
  }

  const needsOrgSelection =
    Array.isArray(activeRoles) && activeRoles.length === 0;

  if (needsOrgSelection) {
    return <Redirect to="/brusers/my-profile/select-organization" />;
  }

  const connectedRoutes = Object.keys(routesConfig).reduce((acc, key) => {
    acc[key] = stripes.connect(routesConfig[key].component);
    return acc;
  }, {});

  return (
    <QueryClientProvider client={queryClient}>
      <AgencyRoutes
        activeRoles={activeRoles}
        path={path}
        connectedRoutes={connectedRoutes}
        parentProps={props}
      />
    </QueryClientProvider>
  );
};

BrAgency.propTypes = {
  match: PropTypes.object.isRequired,
  showSettings: PropTypes.bool,
  stripes: PropTypes.shape({
    connect: PropTypes.func,
  }),
};

export default BrAgency;
